// src/store/slices/complianceSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { postComplianceResourceGraph } from '../../api/complianceApi';
import { AWS_CIS_FOUNDATIONS_V3, AWS_FSBP_V1 } from '../../constants/awsComplianceStandards';
import {
  CLOUD_SCOPE_ALL,
  getCloudScopeConfig,
  getSecondaryScopeConfig,
} from '../../pages/Cloud/cloudScopeConfig';

// 只有支援範圍切換的平台才附加 cloud_scope，後端負責把 'all' 展開成實際的查詢範圍。
// 有第二層範圍的平台（AWS 的 region）再附加對應欄位。
const withCloudScope = (payload, platform, getState) => {
  if (!getCloudScopeConfig(platform).enabled) return payload;

  const { compliance } = getState();
  const next = {
    ...payload,
    cloud_scope: compliance.selectedCloudScope || CLOUD_SCOPE_ALL,
  };

  const secondary = getSecondaryScopeConfig(platform);
  if (secondary) {
    next[secondary.key] = compliance.selectedSecondaryScope || CLOUD_SCOPE_ALL;
  }

  return next;
};

const normalizeWarnings = (warnings) => (Array.isArray(warnings) ? warnings.filter(Boolean) : []);

const syncRealtimeWarnings = (state) => {
  state.realtimeWarnings = Array.from(
    new Set([
      ...normalizeWarnings(state.realtimeWarningSources.summary),
      ...normalizeWarnings(state.realtimeWarningSources.risk),
    ])
  );
};

const initialState = {
  // 三大標準的合規數據
  complianceSummary: {
    // Azure
    'Azure ISO IEC 27001:2022': {
      passed_count: 0,
      total_count: 0,
      failed_count: 0,
      compliance_percentage: 0,
    },
    'CIS Azure Foundations v2.0.0': {
      passed_count: 0,
      total_count: 0,
      failed_count: 0,
      compliance_percentage: 0,
    },
    'Microsoft cloud security benchmark': {
      passed_count: 0,
      total_count: 0,
      failed_count: 0,
      compliance_percentage: 0,
    },
    // GCP
    'ISO 27001 2022': {
      passed_count: 0,
      total_count: 0,
      failed_count: 0,
      compliance_percentage: 0,
    },
    'CIS Google Cloud Platform Foundation v2.0.0': {
      passed_count: 0,
      total_count: 0,
      failed_count: 0,
      compliance_percentage: 0,
    },
    // AWS
    [AWS_CIS_FOUNDATIONS_V3]: {
      passed_count: 0,
      total_count: 0,
      failed_count: 0,
      compliance_percentage: 0,
    },
    [AWS_FSBP_V1]: {
      passed_count: 0,
      total_count: 0,
      failed_count: 0,
      compliance_percentage: 0,
    },
    '金管會雲端資安基準': {
      passed_count: 0,
      total_count: 0,
      failed_count: 0,
      compliance_percentage: 0,
    },
    total: {
      passed_count: 0,
      total_count: 0,
      failed_count: 0,
      compliance_percentage: 0,
    },
  },

  // 標準詳細資料
  complianceDetail: {
    standardId: null,
    standardName: null,
    year: null,
    currentMonth: null,
    currentMonthKey: null,
    months: [],
    controls: [], // 控制項列表
    metadata: {},
  },

  // Assessment 的 Resource
  assessmentResources: {}, // key: assessmentId, value: { data: [], count: 0 }

  // 雲端查詢範圍（Azure subscription / GCP project / AWS account）
  cloudScopes: [], // [{ id, name, state }]
  selectedCloudScope: CLOUD_SCOPE_ALL,
  defaultCloudScopeId: null,
  scopesLoading: false,
  scopesLoaded: false,
  scopesError: null,

  // 第二層查詢範圍（目前只有 AWS 的 region）
  secondaryScopes: [], // [{ id, name, is_primary }]
  selectedSecondaryScope: CLOUD_SCOPE_ALL,
  secondaryScopesLoading: false,
  secondaryScopesError: null,

  // 後端回報的部分失敗（權限不足、某 region/標準查不到），數字可能不完整
  realtimeWarnings: [],
  realtimeWarningSources: {
    summary: [],
    risk: [],
  },

  // 只接受目前畫面最後送出的請求；scope/standard 切換時會同步作廢舊 requestId。
  activeRequestIds: {
    cloudScopes: null,
    secondaryScopes: null,
    summary: null,
    risk: null,
    detail: null,
  },
  activeResourceRequestIds: {},

  // 風險評估數據
  assessments: null,
  selectedStandard: 'total',

  // 統計數據（根據選中的標準）
  statistics: {
    // criticalRisk 為 null 代表這個雲沒有 Critical 等級（Azure），畫面走原本的三級版型
    criticalRisk: null,
    highRisk: 0,
    mediumRisk: 0,
    lowRisk: 0,
    totalRisk: 0,
    passed: 0,
    failed: 0,
    notApplicable: 0,
    total: 0,
    compliancePercentage: 0,
  },

  metadata: {
    cached: false,
    cacheTimestamp: null,
    queryIdentifier: null,
    riskType: 'summary',
    standard: 'all',
    totalRecords: 0,
    count: 0,
    page: 1,
    pageSize: 100,
  },

  loading: false,
  summaryLoading: false,
  detailLoading: false,
  resourcesLoading: false,
  error: null,
};

// 取得此專案憑證可見的查詢範圍清單
export const fetchCloudScopes = createAsyncThunk(
  'compliance/fetchCloudScopes',
  async (params = {}, { rejectWithValue }) => {
    const { platform = 'azure' } = params;
    const { enabled, action } = getCloudScopeConfig(platform);

    if (!enabled || !action) {
      return { scopes: [], default_scope_id: null };
    }

    try {
      const response = await postComplianceResourceGraph({ platform, payload: { action } });
      return response || { scopes: [], default_scope_id: null };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.error || err.response?.data?.message || '取得查詢範圍清單失敗'
      );
    }
  }
);

// 取得第二層範圍清單（AWS region）；需要先選定帳戶才查得到
export const fetchSecondaryScopes = createAsyncThunk(
  'compliance/fetchSecondaryScopes',
  async (params = {}, { rejectWithValue, getState }) => {
    const { platform = 'aws' } = params;
    const secondary = getSecondaryScopeConfig(platform);

    if (!secondary || !secondary.action) {
      return { scopes: [], default_scope_id: null };
    }

    try {
      const payload = {
        action: secondary.action,
        cloud_scope: getState().compliance.selectedCloudScope || CLOUD_SCOPE_ALL,
      };
      const response = await postComplianceResourceGraph({ platform, payload });
      return response || { scopes: [], default_scope_id: null };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.error || err.response?.data?.message || '取得 Region 清單失敗'
      );
    }
  }
);

// 取得三大標準合規摘要
export const fetchComplianceSummary = createAsyncThunk(
  'compliance/fetchComplianceSummary',
  async (params = {}, { rejectWithValue, getState }) => {
    try {
      const { platform = 'azure' } = params;
      const payload = withCloudScope(
        {
          action: 'compliance_summary',
        },
        platform,
        getState
      );
      const response = await postComplianceResourceGraph({ platform, payload });

      if (!response || !response.standards) {
        throw new Error('未收到有效的合規摘要數據');
      }

      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || '取得合規摘要失敗');
    }
  }
);

// 保留原有的風險評估
export const fetchAzureRiskAssessment = createAsyncThunk(
  'compliance/fetchAzureRiskAssessment',
  async (params = {}, { rejectWithValue, getState }) => {
    try {
      const { risk_type = 'summary', standard = 'all', platform = 'azure' } = params;

      const payload = withCloudScope(
        {
          action: 'risk_assessment',
          risk_type,
          standard,
        },
        platform,
        getState
      );
      const response = await postComplianceResourceGraph({ platform, payload });

      if (!response || !response.data || response.data.length === 0) {
        throw new Error('未收到有效的評估資料');
      }

      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || '取得評估資料失敗');
    }
  }
);

// 取得合規標準詳細資料
export const fetchComplianceDetail = createAsyncThunk(
  'compliance/fetchComplianceDetail',
  async ({ standardId, platform = 'azure' }, { rejectWithValue, getState }) => {
    try {
      const payload = withCloudScope(
        {
          action: 'compliance_detail',
          standard_id: standardId,
        },
        platform,
        getState
      );
      const response = await postComplianceResourceGraph({ platform, payload });

      if (!response || !response.data) {
        throw new Error('未收到有效的合規詳細數據');
      }

      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || '取得合規詳細資料失敗');
    }
  }
);

// assessmentResources 的 key：過去月份（讀 DB 該月掃描結果）需與即時資料分開存
export const getAssessmentResourceKey = (assessmentId, scanMonth = null) =>
  scanMonth ? `${assessmentId}|${scanMonth}` : assessmentId;

// 取得 Assessment 的 Resources
export const fetchAssessmentResources = createAsyncThunk(
  'compliance/fetchAssessmentResources',
  async ({ assessmentId, assessmentName, platform = 'azure', scanMonth = null }, { rejectWithValue, getState }) => {
    try {
      const payload = withCloudScope(
        {
          action: 'assessment_resources',
          assessment_id: assessmentId,
          assessment_name: assessmentName,
          ...(scanMonth ? { scan_month: scanMonth } : {}),
        },
        platform,
        getState
      );
      const response = await postComplianceResourceGraph({ platform, payload });

      if (!response) {
        throw new Error('未收到有效的資源數據');
      }

      return {
        resourceKey: getAssessmentResourceKey(assessmentId, scanMonth),
        resources: response,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || '取得資源列表失敗');
    }
  }
);

// 輔助函數：從 API 回應中提取統計資料
const extractStatistics = (apiResponse) => {
  if (!apiResponse || !apiResponse.data || apiResponse.data.length === 0) {
    return {
      criticalRisk: null,
      highRisk: 0,
      mediumRisk: 0,
      lowRisk: 0,
      totalRisk: 0,
      passed: 0,
      failed: 0,
      notApplicable: 0,
      total: 0,
      compliancePercentage: 0,
    };
  }

  const data = apiResponse.data[0];

  return {
    // 沒有這個欄位代表該雲沒有 Critical 等級，用 null 而非 0 才能和「真的是 0」區分
    criticalRisk: data.CriticalRiskCount ?? null,
    highRisk: data.HighRiskCount || 0,
    mediumRisk: data.MediumRiskCount || 0,
    lowRisk: data.LowRiskCount || 0,
    totalRisk: data.TotalRiskCount || 0,
    passed: data.PassedAssessments || 0,
    failed: data.FailedAssessments || 0,
    notApplicable: data.NotApplicableCount || 0,
    total: data.TotalAssessments || 0,
    compliancePercentage: data.CompliancePercentage || 0,
  };
};

// 輔助函數：從 API 回應中提取元資料
const extractMetadata = (apiResponse, params) => {
  return {
    cached: apiResponse.cached || false,
    cacheTimestamp: apiResponse.cache_timestamp || null,
    queryIdentifier: apiResponse.query_identifier || null,
    riskType: apiResponse.risk_type || params.risk_type || 'summary',
    standard: apiResponse.standard || params.standard || 'all',
    totalRecords: apiResponse.total_records || 0,
    count: apiResponse.count || 0,
    page: apiResponse.page || 1,
    pageSize: apiResponse.page_size || 100,
  };
};

const complianceSlice = createSlice({
  name: 'compliance',
  initialState,
  reducers: {
    setSelectedStandard: (state, action) => {
      state.selectedStandard = action.payload;
      state.loading = false;
      state.detailLoading = false;
      state.activeRequestIds.risk = null;
      state.activeRequestIds.detail = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetAssessments: (state) => {
      state.assessments = null;
      state.statistics = initialState.statistics;
      state.metadata = initialState.metadata;
      state.loading = false;
      state.activeRequestIds.risk = null;
      state.realtimeWarningSources.risk = [];
      syncRealtimeWarnings(state);
    },

    // 清除合規詳細資料
    clearComplianceDetail: (state) => {
      state.complianceDetail = initialState.complianceDetail;
      state.assessmentResources = {};
      state.detailLoading = false;
      state.activeRequestIds.detail = null;
      state.activeResourceRequestIds = {};
      state.resourcesLoading = false;
    },

    setSelectedCloudScope: (state, action) => {
      state.selectedCloudScope = action.payload || CLOUD_SCOPE_ALL;
      // 資源清單是依範圍查出來的，換範圍後既有快取不再適用
      state.assessmentResources = {};
      // 第二層範圍（region）是依帳戶查的，換帳戶後要重查並回到「全部」
      state.secondaryScopes = [];
      state.selectedSecondaryScope = CLOUD_SCOPE_ALL;
      state.secondaryScopesError = null;
      state.realtimeWarnings = [];
      state.realtimeWarningSources = { summary: [], risk: [] };
      state.summaryLoading = false;
      state.loading = false;
      state.detailLoading = false;
      state.activeRequestIds.secondaryScopes = null;
      state.activeRequestIds.summary = null;
      state.activeRequestIds.risk = null;
      state.activeRequestIds.detail = null;
      state.activeResourceRequestIds = {};
      state.resourcesLoading = false;
    },

    setSelectedSecondaryScope: (state, action) => {
      state.selectedSecondaryScope = action.payload || CLOUD_SCOPE_ALL;
      state.assessmentResources = {};
      state.realtimeWarnings = [];
      state.realtimeWarningSources = { summary: [], risk: [] };
      state.summaryLoading = false;
      state.loading = false;
      state.detailLoading = false;
      state.activeRequestIds.summary = null;
      state.activeRequestIds.risk = null;
      state.activeRequestIds.detail = null;
      state.activeResourceRequestIds = {};
      state.resourcesLoading = false;
    },

    // 切換平台時清掉上一個雲的範圍狀態
    resetCloudScopes: (state) => {
      state.cloudScopes = [];
      state.selectedCloudScope = CLOUD_SCOPE_ALL;
      state.defaultCloudScopeId = null;
      state.scopesLoading = false;
      state.scopesLoaded = false;
      state.scopesError = null;
      state.assessmentResources = {};
      state.secondaryScopes = [];
      state.selectedSecondaryScope = CLOUD_SCOPE_ALL;
      state.secondaryScopesLoading = false;
      state.secondaryScopesError = null;
      state.realtimeWarnings = [];
      state.realtimeWarningSources = { summary: [], risk: [] };
      state.summaryLoading = false;
      state.loading = false;
      state.detailLoading = false;
      state.activeRequestIds = { ...initialState.activeRequestIds };
      state.activeResourceRequestIds = {};
      state.resourcesLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchCloudScopes
      .addCase(fetchCloudScopes.pending, (state, action) => {
        state.scopesLoading = true;
        state.scopesError = null;
        state.activeRequestIds.cloudScopes = action.meta.requestId;
      })
      .addCase(fetchCloudScopes.fulfilled, (state, action) => {
        if (state.activeRequestIds.cloudScopes !== action.meta.requestId) return;

        // Azure 回 subscriptions / default_subscription_id，其他雲回通用的 scopes / default_scope_id
        const payload = action.payload || {};
        const scopes = payload.scopes || payload.subscriptions || [];
        const defaultScopeId =
          payload.default_scope_id || payload.default_subscription_id || null;
        // 後端明講不支援「全部」時（AWS），一定要落在某一個具體範圍上
        const supportsAll = payload.supports_all !== false;

        state.scopesLoading = false;
        state.activeRequestIds.cloudScopes = null;
        state.scopesLoaded = true;
        state.scopesError = null;
        state.cloudScopes = scopes;
        state.defaultCloudScopeId = defaultScopeId;

        const stillValid = scopes.some((scope) => scope.id === state.selectedCloudScope);
        if (!stillValid) {
          if (supportsAll) {
            // 目前選取的範圍已不在清單中（例如權限異動）就回到「全部」
            state.selectedCloudScope = CLOUD_SCOPE_ALL;
          } else {
            state.selectedCloudScope = defaultScopeId || scopes[0]?.id || CLOUD_SCOPE_ALL;
          }
        }
      })
      .addCase(fetchCloudScopes.rejected, (state, action) => {
        if (state.activeRequestIds.cloudScopes !== action.meta.requestId) return;

        state.scopesLoading = false;
        state.activeRequestIds.cloudScopes = null;
        // 清單失敗時仍讓頁面以「全部」運作（後端會回退到預設訂閱／主帳戶）
        state.scopesLoaded = true;
        state.scopesError = action.payload || '取得查詢範圍清單失敗';
        state.cloudScopes = [];
        state.selectedCloudScope = CLOUD_SCOPE_ALL;
      })

      // fetchSecondaryScopes（AWS region）
      .addCase(fetchSecondaryScopes.pending, (state, action) => {
        state.secondaryScopesLoading = true;
        state.secondaryScopesError = null;
        state.activeRequestIds.secondaryScopes = action.meta.requestId;
      })
      .addCase(fetchSecondaryScopes.fulfilled, (state, action) => {
        if (state.activeRequestIds.secondaryScopes !== action.meta.requestId) return;

        const payload = action.payload || {};
        const scopes = payload.scopes || [];

        state.secondaryScopesLoading = false;
        state.activeRequestIds.secondaryScopes = null;
        state.secondaryScopesError = null;
        state.secondaryScopes = scopes;

        if (
          state.selectedSecondaryScope !== CLOUD_SCOPE_ALL &&
          !scopes.some((scope) => scope.id === state.selectedSecondaryScope)
        ) {
          state.selectedSecondaryScope = CLOUD_SCOPE_ALL;
        }
      })
      .addCase(fetchSecondaryScopes.rejected, (state, action) => {
        if (state.activeRequestIds.secondaryScopes !== action.meta.requestId) return;

        state.secondaryScopesLoading = false;
        state.activeRequestIds.secondaryScopes = null;
        state.secondaryScopesError = action.payload || '取得 Region 清單失敗';
        state.secondaryScopes = [];
        state.selectedSecondaryScope = CLOUD_SCOPE_ALL;
      })

      // fetchComplianceSummary
      .addCase(fetchComplianceSummary.pending, (state, action) => {
        state.summaryLoading = true;
        state.error = null;
        state.activeRequestIds.summary = action.meta.requestId;
      })
      .addCase(fetchComplianceSummary.fulfilled, (state, action) => {
        if (state.activeRequestIds.summary !== action.meta.requestId) return;

        state.summaryLoading = false;
        state.activeRequestIds.summary = null;
        state.realtimeWarningSources.summary = normalizeWarnings(action.payload.warnings);
        syncRealtimeWarnings(state);

        // 更新各個標準的數據
        const standards = action.payload.standards || {};
        state.complianceSummary = {
          ...state.complianceSummary,
          ...standards,
        };
        // 根據平台計算總和
        const platform = action.meta?.arg?.platform || 'azure';

        if (platform === 'gcp') {
          const iso = standards['ISO 27001 2022'] || { passed_count: 0, total_count: 0, failed_count: 0 };
          const cis =
            standards['CIS Google Cloud Platform Foundation v2.0.0'] || {
              passed_count: 0,
              total_count: 0,
              failed_count: 0,
            };

          const totalPassed = iso.passed_count + cis.passed_count;
          const totalCount = iso.total_count + cis.total_count;
          const totalFailed = iso.failed_count + cis.failed_count;
          const totalPercentage = totalCount > 0 ? Math.round((totalPassed / totalCount) * 100 * 100) / 100 : 0;

          state.complianceSummary['total'] = {
            passed_count: totalPassed,
            total_count: totalCount,
            failed_count: totalFailed,
            compliance_percentage: totalPercentage,
          };
        } else if (platform === 'aws') {
          const cis =
            standards[AWS_CIS_FOUNDATIONS_V3] || {
              passed_count: 0,
              total_count: 0,
              failed_count: 0,
            };
          const fsbp =
            standards[AWS_FSBP_V1] || {
              passed_count: 0,
              total_count: 0,
              failed_count: 0,
            };

          const totalPassed = cis.passed_count + fsbp.passed_count;
          const totalCount = cis.total_count + fsbp.total_count;
          const totalFailed = cis.failed_count + fsbp.failed_count;
          const totalPercentage = totalCount > 0 ? Math.round((totalPassed / totalCount) * 100 * 100) / 100 : 0;

          state.complianceSummary['total'] = {
            passed_count: totalPassed,
            total_count: totalCount,
            failed_count: totalFailed,
            compliance_percentage: totalPercentage,
          };
        } else {
          const iso =
            standards['Azure ISO IEC 27001:2022'] || { passed_count: 0, total_count: 0, failed_count: 0 };
          const cis =
            standards['CIS Azure Foundations v2.0.0'] || { passed_count: 0, total_count: 0, failed_count: 0 };
          const mcsb =
            standards['Microsoft cloud security benchmark'] || {
              passed_count: 0,
              total_count: 0,
              failed_count: 0,
            };

          const totalPassed = iso.passed_count + cis.passed_count + mcsb.passed_count;
          const totalCount = iso.total_count + cis.total_count + mcsb.total_count;
          const totalFailed = iso.failed_count + cis.failed_count + mcsb.failed_count;
          const totalPercentage = totalCount > 0 ? Math.round((totalPassed / totalCount) * 100 * 100) / 100 : 0;

          state.complianceSummary['total'] = {
            passed_count: totalPassed,
            total_count: totalCount,
            failed_count: totalFailed,
            compliance_percentage: totalPercentage,
          };
        }
      })
      .addCase(fetchComplianceSummary.rejected, (state, action) => {
        if (state.activeRequestIds.summary !== action.meta.requestId) return;

        state.summaryLoading = false;
        state.activeRequestIds.summary = null;
        state.error = action.payload;
        state.realtimeWarningSources.summary = [];
        syncRealtimeWarnings(state);
      })

      // fetchAzureRiskAssessment
      .addCase(fetchAzureRiskAssessment.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.activeRequestIds.risk = action.meta.requestId;
      })
      .addCase(fetchAzureRiskAssessment.fulfilled, (state, action) => {
        if (state.activeRequestIds.risk !== action.meta.requestId) return;

        state.loading = false;
        state.activeRequestIds.risk = null;
        state.assessments = action.payload.data[0] || null;
        state.realtimeWarningSources.risk = normalizeWarnings(action.payload.warnings);
        syncRealtimeWarnings(state);
        state.statistics = extractStatistics(action.payload);
        state.metadata = extractMetadata(action.payload, {
          risk_type: state.metadata.riskType,
          standard: state.selectedStandard,
        });
      })
      .addCase(fetchAzureRiskAssessment.rejected, (state, action) => {
        if (state.activeRequestIds.risk !== action.meta.requestId) return;

        state.loading = false;
        state.activeRequestIds.risk = null;
        state.error = action.payload;
        state.assessments = null;
        state.statistics = initialState.statistics;
        state.realtimeWarningSources.risk = [];
        syncRealtimeWarnings(state);
      })

      // 處理 fetchComplianceDetail
      .addCase(fetchComplianceDetail.pending, (state, action) => {
        state.detailLoading = true;
        state.error = null;
        state.activeRequestIds.detail = action.meta.requestId;
      })
      .addCase(fetchComplianceDetail.fulfilled, (state, action) => {
        if (state.activeRequestIds.detail !== action.meta.requestId) return;

        state.detailLoading = false;
        state.activeRequestIds.detail = null;
        state.complianceDetail = {
          standardId: action.payload.standard_id,
          standardName: action.payload.standard_name,
          year: action.payload.year,
          currentMonth: action.payload.current_month,
          currentMonthKey: action.payload.current_month_key || null,
          months: action.payload.months || [],
          controls: action.payload.data || [],
          metadata: action.payload.metadata || {},
        };
      })
      .addCase(fetchComplianceDetail.rejected, (state, action) => {
        if (state.activeRequestIds.detail !== action.meta.requestId) return;

        state.detailLoading = false;
        state.activeRequestIds.detail = null;
        state.error = action.payload;
      });

    // 處理 fetchAssessmentResources
    builder
      .addCase(fetchAssessmentResources.pending, (state, action) => {
        const { assessmentId, scanMonth } = action.meta?.arg || {};
        if (assessmentId) {
          state.activeResourceRequestIds[getAssessmentResourceKey(assessmentId, scanMonth)] = action.meta.requestId;
        }
        state.resourcesLoading = true;
      })
      .addCase(fetchAssessmentResources.fulfilled, (state, action) => {
        const { resourceKey, resources } = action.payload;
        if (state.activeResourceRequestIds[resourceKey] !== action.meta.requestId) return;

        delete state.activeResourceRequestIds[resourceKey];
        state.resourcesLoading = Object.keys(state.activeResourceRequestIds).length > 0;
        state.assessmentResources[resourceKey] = resources;
      })
      .addCase(fetchAssessmentResources.rejected, (state, action) => {
        const { assessmentId, scanMonth } = action.meta?.arg || {};
        const resourceKey = assessmentId ? getAssessmentResourceKey(assessmentId, scanMonth) : null;
        if (!resourceKey || state.activeResourceRequestIds[resourceKey] !== action.meta.requestId) return;

        delete state.activeResourceRequestIds[resourceKey];
        state.resourcesLoading = Object.keys(state.activeResourceRequestIds).length > 0;
        state.error = action.payload;
      });
  },
});

export const {
  setSelectedStandard,
  clearError,
  resetAssessments,
  clearComplianceDetail,
  setSelectedCloudScope,
  setSelectedSecondaryScope,
  resetCloudScopes,
} = complianceSlice.actions;
export default complianceSlice.reducer;
